import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Link } from '@mui/material'
import { memo, type ReactNode } from 'react'

import { borderRadius } from '../../../foundations/border-radius-tokens.ts'
import { logo } from '../../../foundations/logo.ts'
import { spacing } from '../../../foundations/spacing-tokens.ts'
import { useCoBranding } from '../../co-branding/hooks.ts'
import { useForwardUtm } from '../../hooks/use-forward-utm.ts'

type HorizontalLogoProps = {
  width?: number | undefined
  height?: number | undefined
  className?: string | undefined
  color: string
}

export const HorizontalLogo = memo<HorizontalLogoProps>(function HorizontalLogo(props) {
  const { width, height, className, color } = props
  const coBranding = useCoBranding()
  const { aspectRatio, ImageComponent } = coBranding.horizontalLogo

  let widthToApply
  let heightToApply
  if (width == undefined && height != undefined) {
    widthToApply = height * aspectRatio
    heightToApply = height
  } else if (width != undefined && height == undefined) {
    widthToApply = width
    heightToApply = width / aspectRatio
  } else if (width != undefined && height != undefined) {
    throw new Error('Cannot specify both width and height')
  } else {
    throw new Error('Must specify either width or height')
  }

  const image = (
    <ImageComponent {...(className ? { className } : {})} width={widthToApply} height={heightToApply} color={color} />
  )

  return coBranding.showByOrusInHeader ? (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: ${spacing[50]};
      `}
    >
      {image}
      <ByOrus prefix={coBranding.showByOrusInHeader} />
    </div>
  ) : (
    <HomepageLogoLink>{image}</HomepageLogoLink>
  )
})

const HomepageLogoLink = memo<{ children: ReactNode }>(function HomepageLogoLink({ children }) {
  // @TODO: remove this router coupling — `useForwardUtm` depends on TanStack Router via `useSearch`,
  // which leaks an application concern into the design system. Lift the href computation to the caller
  // (pass `href` as a prop / coBranding option) so pharaoh stops importing `@tanstack/react-router`.
  const forwardUtm = useForwardUtm()
  return <LogoLink href={forwardUtm('https://www.orus.eu/')}>{children}</LogoLink>
})

const ByOrus = memo<{ prefix: 'by' | 'with' }>(function ByOrus({ prefix }) {
  return (
    <div
      css={css`
        display: flex;
        padding: 7px 10px;
        align-items: baseline;
        gap: ${spacing[20]};
        background-color: #e2dcfe;
        border-radius: ${borderRadius['20']};
      `}
    >
      <span
        css={css`
          color: #151318;
          text-align: center;
          font-family: Poppins;
          font-size: 12px;
          font-style: normal;
          font-weight: 400;
          line-height: 18px;
          letter-spacing: 0.3px;
        `}
      >
        {prefix}
      </span>
      <img src={logo.WordmarkDark} alt="WordmarkDark" width={32.5} height={10} />
    </div>
  )
})

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
`
